import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, X } from 'lucide-react';
import type {
  GrClaimCostRule,
  GrGameConfigDetail,
  GrItemListFormat,
  GrLeaderboardType,
  GrRewardMode,
} from '@/lib/api';
import { useGameConfigs, useUpdateGameConfig } from '../hooks';

interface GameConfigEditorProps {
  game: GrGameConfigDetail;
  onSaved: () => void;
  /** When true, the linkedArcheId / leaderboardTargetGameId / chatGuildId
   *  fields are editable. The server enforces platform-admin-only on these
   *  regardless; this just hides them from non-platform admins. */
  isPlatformAdmin: boolean;
}

const REWARD_MODES: GrRewardMode[] = ['standard', 'item_list', 'recipe'];
const ITEM_LIST_FORMATS: GrItemListFormat[] = ['repeated', 'counted'];
const LEADERBOARD_TYPES: GrLeaderboardType[] = ['high_score', 'low_score', 'none'];

const NONE_VALUE = '__none__';

/**
 * Inline editor for a gr_game_configs row, split into three save-independent
 * slices so the admin can edit one section without touching the others. Each
 * slice tracks its own dirty state and disables Save until the user changes
 * something. Cap-increasing edits flip review_status server-side; the page
 * surface invalidates the query so the badge updates after Save.
 */
export function GameConfigEditor({ game, onSaved, isPlatformAdmin }: GameConfigEditorProps) {
  return (
    <div className="space-y-4">
      <GeneralSection game={game} onSaved={onSaved} isPlatformAdmin={isPlatformAdmin} />
      <ItemsAndModeSection game={game} onSaved={onSaved} />
      <ScoreAndCostsSection game={game} onSaved={onSaved} />
    </div>
  );
}

// ============================================================================
// General section: displayName/description/active/leaderboardType/rewardMode/
// rewardLookupEvent/linkedArcheId/leaderboardTargetGameId/chatGuildId
// ============================================================================

function GeneralSection({
  game,
  onSaved,
  isPlatformAdmin,
}: {
  game: GrGameConfigDetail;
  onSaved: () => void;
  isPlatformAdmin: boolean;
}) {
  const updateMutation = useUpdateGameConfig();
  const { gameConfigs: allGames } = useGameConfigs();
  const [displayName, setDisplayName] = useState(game.displayName);
  const [description, setDescription] = useState(game.description ?? '');
  const [isActive, setIsActive] = useState(game.isActive);
  const [leaderboardType, setLeaderboardType] = useState<GrLeaderboardType>(game.leaderboardType);
  const [rewardMode, setRewardMode] = useState<GrRewardMode>(game.rewardMode);
  const [rewardLookupEvent, setRewardLookupEvent] = useState<string>(game.rewardLookupEvent ?? '');
  const [linkedArcheId, setLinkedArcheId] = useState<string>(game.linkedArcheId ?? '');
  const [leaderboardTargetGameId, setLeaderboardTargetGameId] = useState<string>(
    game.leaderboardTargetGameId ?? NONE_VALUE,
  );
  const [chatGuildId, setChatGuildId] = useState<string>(game.chatGuildId ?? '');
  const [includeInLitcraftReset, setIncludeInLitcraftReset] = useState<boolean>(
    game.includeInLitcraftReset === true,
  );
  const [error, setError] = useState<string | null>(null);

  // Re-sync local state only when the row genuinely changed server-side
  // (review I11). Keying on the full `game` object would re-run after every
  // save in any section — the parent re-fetches, the new object is a fresh
  // reference even when fields are identical, and unsaved edits in this
  // section get clobbered. Keying on (gameConfigId, updatedAt) means the
  // effect fires only when the row really moved on the server.
  useEffect(() => {
    setDisplayName(game.displayName);
    setDescription(game.description ?? '');
    setIsActive(game.isActive);
    setLeaderboardType(game.leaderboardType);
    setRewardMode(game.rewardMode);
    setRewardLookupEvent(game.rewardLookupEvent ?? '');
    setLinkedArcheId(game.linkedArcheId ?? '');
    setLeaderboardTargetGameId(game.leaderboardTargetGameId ?? NONE_VALUE);
    setChatGuildId(game.chatGuildId ?? '');
    setIncludeInLitcraftReset(game.includeInLitcraftReset === true);
  }, [game.gameConfigId, game.updatedAt]);

  // Eligible leaderboard targets: any other game whose own redirect is null.
  // (Single-hop chain rule — server enforces this too.)
  const targetOptions = useMemo(
    () =>
      allGames
        .filter((g) => g.gameConfigId !== game.gameConfigId && !g.leaderboardTargetGameId)
        .sort((a, b) =>
          a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }),
        ),
    [allGames, game.gameConfigId],
  );

  const dirty =
    displayName !== game.displayName ||
    (description || '') !== (game.description ?? '') ||
    isActive !== game.isActive ||
    leaderboardType !== game.leaderboardType ||
    rewardMode !== game.rewardMode ||
    rewardLookupEvent !== (game.rewardLookupEvent ?? '') ||
    linkedArcheId !== (game.linkedArcheId ?? '') ||
    chatGuildId !== (game.chatGuildId ?? '') ||
    includeInLitcraftReset !== (game.includeInLitcraftReset === true) ||
    (leaderboardTargetGameId === NONE_VALUE ? null : leaderboardTargetGameId) !==
      game.leaderboardTargetGameId;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const input: Parameters<typeof updateMutation.mutateAsync>[0]['input'] = {
        displayName: displayName !== game.displayName ? displayName : undefined,
        description:
          (description || '') !== (game.description ?? '') ? description || '' : undefined,
        isActive: isActive !== game.isActive ? isActive : undefined,
        leaderboardType: leaderboardType !== game.leaderboardType ? leaderboardType : undefined,
        rewardMode: rewardMode !== game.rewardMode ? rewardMode : undefined,
        rewardLookupEvent:
          rewardLookupEvent !== (game.rewardLookupEvent ?? '')
            ? rewardLookupEvent.trim() || null
            : undefined,
        includeInLitcraftReset:
          includeInLitcraftReset !== (game.includeInLitcraftReset === true)
            ? includeInLitcraftReset
            : undefined,
      };
      if (isPlatformAdmin) {
        if (linkedArcheId !== (game.linkedArcheId ?? '')) {
          input.linkedArcheId = linkedArcheId.trim() || null;
        }
        const desiredTarget =
          leaderboardTargetGameId === NONE_VALUE ? null : leaderboardTargetGameId;
        if (desiredTarget !== game.leaderboardTargetGameId) {
          input.leaderboardTargetGameId = desiredTarget;
        }
        if (chatGuildId !== (game.chatGuildId ?? '')) {
          input.chatGuildId = chatGuildId.trim() || null;
        }
      }
      await updateMutation.mutateAsync({ gameConfigId: game.gameConfigId, input });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">General</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cfg-display-name">Display name</Label>
              <Input
                id="cfg-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cfg-active">Active</Label>
              <div className="flex items-center gap-2 h-10">
                <input
                  id="cfg-active"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded border-input"
                />
                <span className="text-sm text-muted-foreground">
                  Inactive games reject all claims at the gateway.
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cfg-litcraft-reset">Included in LitCraft tester reset</Label>
            <div className="flex items-start gap-2">
              <input
                id="cfg-litcraft-reset"
                type="checkbox"
                checked={includeInLitcraftReset}
                onChange={(e) => setIncludeInLitcraftReset(e.target.checked)}
                className="rounded border-input mt-1"
              />
              <span className="text-sm text-muted-foreground">
                Tick for LitCraft games. A tester's account reset (staging only) then clears this
                game's saves, quests and reward history for that tester. Leave unticked for games
                that are not LitCraft (Flappyland, Fiardle): their data survives a reset.
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cfg-description">Description</Label>
            <Textarea
              id="cfg-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cfg-leaderboard-type">Leaderboard type</Label>
              <Select
                value={leaderboardType}
                onValueChange={(v) => setLeaderboardType(v as GrLeaderboardType)}
              >
                <SelectTrigger id="cfg-leaderboard-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEADERBOARD_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cfg-reward-mode">Reward mode</Label>
              <Select value={rewardMode} onValueChange={(v) => setRewardMode(v as GrRewardMode)}>
                <SelectTrigger id="cfg-reward-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REWARD_MODES.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cfg-reward-lookup">Reward lookup event</Label>
              <Input
                id="cfg-reward-lookup"
                value={rewardLookupEvent}
                onChange={(e) => setRewardLookupEvent(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
          {isPlatformAdmin && (
            <>
              <div className="space-y-2">
                <Label htmlFor="cfg-linked-arche">
                  Linked plugin arche{' '}
                  <Badge variant="outline" className="ml-1">
                    Platform admin
                  </Badge>
                </Label>
                <Input
                  id="cfg-linked-arche"
                  value={linkedArcheId}
                  onChange={(e) => setLinkedArcheId(e.target.value)}
                  placeholder="arch_… (leave blank to unlink)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-leaderboard-target">
                  Leaderboard target game{' '}
                  <Badge variant="outline" className="ml-1">
                    Platform admin
                  </Badge>
                </Label>
                <Select
                  value={leaderboardTargetGameId}
                  onValueChange={(v) => setLeaderboardTargetGameId(v)}
                >
                  <SelectTrigger id="cfg-leaderboard-target">
                    <SelectValue placeholder="This game owns its own leaderboard" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>This game owns its own leaderboard</SelectItem>
                    {targetOptions.map((g) => (
                      <SelectItem key={g.gameConfigId} value={g.gameConfigId}>
                        {g.displayName} ({g.gameName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  When set, this game's claims still appear in its own reward log, but its
                  leaderboard scores merge into the target game's leaderboard. Single-hop only — the
                  target must own its own leaderboard. Setting or changing this flips the game back
                  to pending review.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-chat-guild">
                  Guild chat{' '}
                  <Badge variant="outline" className="ml-1">
                    Platform admin
                  </Badge>
                </Label>
                <Input
                  id="cfg-chat-guild"
                  value={chatGuildId}
                  onChange={(e) => setChatGuildId(e.target.value)}
                  placeholder="gld_… (leave blank for none)"
                />
                <p className="text-xs text-muted-foreground">
                  The guild whose chat the linked plugin shows its players, and lets them join. It
                  must be active and public, and its owner must have turned on plugin sharing in
                  Guild Management.
                </p>
              </div>
            </>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={!dirty || updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save general
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Items & Mode section: itemListFormat, allowedItems, allowedItemPrefixes,
// claimStateKeyword
// ============================================================================

function ItemsAndModeSection({ game, onSaved }: { game: GrGameConfigDetail; onSaved: () => void }) {
  const updateMutation = useUpdateGameConfig();
  const [itemListFormat, setItemListFormat] = useState<GrItemListFormat>(
    game.itemListFormat ?? 'repeated',
  );
  const [allowedItems, setAllowedItems] = useState<string[] | null>(game.allowedItems);
  const [allowedItemPrefixes, setAllowedItemPrefixes] = useState<string[] | null>(
    game.allowedItemPrefixes,
  );
  const [claimStateKeyword, setClaimStateKeyword] = useState<string>(game.claimStateKeyword ?? '');
  const [error, setError] = useState<string | null>(null);
  const [newItem, setNewItem] = useState('');
  const [newPrefix, setNewPrefix] = useState('');

  useEffect(() => {
    setItemListFormat(game.itemListFormat ?? 'repeated');
    setAllowedItems(game.allowedItems);
    setAllowedItemPrefixes(game.allowedItemPrefixes);
    setClaimStateKeyword(game.claimStateKeyword ?? '');
  }, [game.gameConfigId, game.updatedAt]);

  const arraysEqual = (a: string[] | null, b: string[] | null) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };

  const dirty =
    itemListFormat !== (game.itemListFormat ?? 'repeated') ||
    !arraysEqual(allowedItems, game.allowedItems) ||
    !arraysEqual(allowedItemPrefixes, game.allowedItemPrefixes) ||
    claimStateKeyword !== (game.claimStateKeyword ?? '');

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const input: Parameters<typeof updateMutation.mutateAsync>[0]['input'] = {};
      if (itemListFormat !== (game.itemListFormat ?? 'repeated')) {
        input.itemListFormat = itemListFormat;
      }
      if (!arraysEqual(allowedItems, game.allowedItems)) {
        input.allowedItems = allowedItems && allowedItems.length > 0 ? allowedItems : null;
      }
      if (!arraysEqual(allowedItemPrefixes, game.allowedItemPrefixes)) {
        input.allowedItemPrefixes =
          allowedItemPrefixes && allowedItemPrefixes.length > 0 ? allowedItemPrefixes : null;
      }
      if (claimStateKeyword !== (game.claimStateKeyword ?? '')) {
        input.claimStateKeyword = claimStateKeyword.trim() || null;
      }
      await updateMutation.mutateAsync({ gameConfigId: game.gameConfigId, input });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  // Dedupe on add (review I17) so chip-list keys are unique by name and we
  // never ship duplicate strings to the server.
  const addItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    const current = allowedItems ?? [];
    if (current.includes(trimmed)) {
      setNewItem('');
      return;
    }
    setAllowedItems([...current, trimmed]);
    setNewItem('');
  };
  const removeItem = (idx: number) => {
    setAllowedItems((allowedItems ?? []).filter((_, i) => i !== idx));
  };
  const addPrefix = () => {
    const trimmed = newPrefix.trim();
    if (!trimmed) return;
    const current = allowedItemPrefixes ?? [];
    if (current.includes(trimmed)) {
      setNewPrefix('');
      return;
    }
    setAllowedItemPrefixes([...current, trimmed]);
    setNewPrefix('');
  };
  const removePrefix = (idx: number) => {
    setAllowedItemPrefixes((allowedItemPrefixes ?? []).filter((_, i) => i !== idx));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Items &amp; Mode</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cfg-item-list-format">Item-list wire format</Label>
              <Select
                value={itemListFormat}
                onValueChange={(v) => setItemListFormat(v as GrItemListFormat)}
              >
                <SelectTrigger id="cfg-item-list-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_LIST_FORMATS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cfg-state-keyword">Claim state keyword</Label>
              <Input
                id="cfg-state-keyword"
                value={claimStateKeyword}
                onChange={(e) => setClaimStateKeyword(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Allowed items (by name)</Label>
            <div className="flex flex-wrap gap-1">
              {(allowedItems ?? []).map((name, i) => (
                <Badge key={name} variant="secondary" className="gap-1">
                  {name}
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    onClick={() => removeItem(i)}
                    className="hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {(!allowedItems || allowedItems.length === 0) && (
                <span className="text-xs text-muted-foreground">No restriction</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                placeholder="Heptal"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addItem();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addItem}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Allowed item prefixes</Label>
            <div className="flex flex-wrap gap-1">
              {(allowedItemPrefixes ?? []).map((p, i) => (
                <Badge key={p} variant="secondary" className="gap-1">
                  {p}*
                  <button
                    type="button"
                    aria-label={`Remove ${p}`}
                    onClick={() => removePrefix(i)}
                    className="hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {(!allowedItemPrefixes || allowedItemPrefixes.length === 0) && (
                <span className="text-xs text-muted-foreground">No prefix restrictions</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={newPrefix}
                onChange={(e) => setNewPrefix(e.target.value)}
                placeholder="Pickaxe"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPrefix();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addPrefix}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={!dirty || updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save items &amp; mode
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Score & Costs section: minScore/maxScore/claimCosts
// ============================================================================

/**
 * Structural compare for ClaimCostRule lists. Avoids `JSON.stringify` (review
 * I14) — key ordering, null vs undefined, and missing-key handling all
 * produce false-dirty signals on a no-op load. We compare each field
 * explicitly against the canonical shape.
 */
function claimCostRulesEqual(a: GrClaimCostRule[] | null, b: GrClaimCostRule[] | null): boolean {
  if (a === b) return true;
  const aArr = a ?? [];
  const bArr = b ?? [];
  if (aArr.length !== bArr.length) return false;
  for (let i = 0; i < aArr.length; i++) {
    const x = aArr[i];
    const y = bArr[i];
    if (x.pattern !== y.pattern) return false;
    if (x.currency !== y.currency) return false;
    if (x.amount !== y.amount) return false;
    if ((x.value ?? null) !== (y.value ?? null)) return false;
    const xNames = x.names ?? [];
    const yNames = y.names ?? [];
    if (xNames.length !== yNames.length) return false;
    for (let j = 0; j < xNames.length; j++) {
      if (xNames[j] !== yNames[j]) return false;
    }
  }
  return true;
}

function ScoreAndCostsSection({
  game,
  onSaved,
}: {
  game: GrGameConfigDetail;
  onSaved: () => void;
}) {
  const updateMutation = useUpdateGameConfig();
  // String-mirror score inputs (review C4). Using `Number(e.target.value)`
  // directly silently sets the field to 0 when the user clears it, which is
  // both surprising in the UI and a data-integrity hazard. Track raw text
  // and parse on submit.
  const [minScoreText, setMinScoreText] = useState<string>(String(game.minScore));
  const [maxScoreText, setMaxScoreText] = useState<string>(String(game.maxScore));
  const [claimCosts, setClaimCosts] = useState<GrClaimCostRule[] | null>(game.claimCosts);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMinScoreText(String(game.minScore));
    setMaxScoreText(String(game.maxScore));
    setClaimCosts(game.claimCosts);
  }, [game.gameConfigId, game.updatedAt]);

  const parsedMinScore = Number.parseInt(minScoreText, 10);
  const parsedMaxScore = Number.parseInt(maxScoreText, 10);
  const minScoreValid = !Number.isNaN(parsedMinScore);
  const maxScoreValid = !Number.isNaN(parsedMaxScore);

  const minDirty = minScoreValid && parsedMinScore !== game.minScore;
  const maxDirty = maxScoreValid && parsedMaxScore !== game.maxScore;

  const dirty = minDirty || maxDirty || !claimCostRulesEqual(claimCosts, game.claimCosts);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (minDirty || !minScoreValid) {
      // Block submit if user cleared the field. (`!minScoreValid` covers
      // empty string, "abc", etc.) Without this gate, a missing-min-score
      // submit would silently send the existing value but skip the dirty
      // flag — confusing.
      if (!minScoreValid) {
        setError('Min score must be a number');
        return;
      }
    }
    if (maxDirty || !maxScoreValid) {
      if (!maxScoreValid) {
        setError('Max score must be a number');
        return;
      }
    }
    try {
      const input: Parameters<typeof updateMutation.mutateAsync>[0]['input'] = {};
      if (minDirty) input.minScore = parsedMinScore;
      if (maxDirty) input.maxScore = parsedMaxScore;
      if (!claimCostRulesEqual(claimCosts, game.claimCosts)) {
        input.claimCosts = claimCosts && claimCosts.length > 0 ? claimCosts : null;
      }
      await updateMutation.mutateAsync({ gameConfigId: game.gameConfigId, input });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const addCostRule = () => {
    setClaimCosts([
      ...(claimCosts ?? []),
      { pattern: 'exact', names: [], currency: 'Heptal', amount: 0 },
    ]);
  };
  const removeCostRule = (idx: number) => {
    setClaimCosts((claimCosts ?? []).filter((_, i) => i !== idx));
  };
  const updateCostRule = (idx: number, partial: Partial<GrClaimCostRule>) => {
    setClaimCosts((claimCosts ?? []).map((r, i) => (i === idx ? { ...r, ...partial } : r)));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Score &amp; Costs</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cfg-min-score">Min score</Label>
              <Input
                id="cfg-min-score"
                type="number"
                value={minScoreText}
                onChange={(e) => setMinScoreText(e.target.value)}
                aria-invalid={!minScoreValid || undefined}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cfg-max-score">Max score</Label>
              <Input
                id="cfg-max-score"
                type="number"
                value={maxScoreText}
                onChange={(e) => setMaxScoreText(e.target.value)}
                aria-invalid={!maxScoreValid || undefined}
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Claim cost rules</Label>
              <Button type="button" variant="outline" size="sm" onClick={addCostRule}>
                <Plus className="h-3 w-3 mr-1" />
                Add rule
              </Button>
            </div>
            {(!claimCosts || claimCosts.length === 0) && (
              <p className="text-xs text-muted-foreground">No cost rules — all claims are free.</p>
            )}
            {(claimCosts ?? []).map((rule, idx) => (
              <Card key={idx} className="border-dashed">
                <CardContent className="p-3 space-y-2">
                  <div className="flex justify-between items-center gap-2">
                    <Select
                      value={rule.pattern}
                      onValueChange={(v) =>
                        updateCostRule(idx, { pattern: v as GrClaimCostRule['pattern'] })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exact">exact</SelectItem>
                        <SelectItem value="prefix">prefix</SelectItem>
                      </SelectContent>
                    </Select>
                    {rule.pattern === 'exact' ? (
                      <Input
                        placeholder="Comma-separated item names"
                        value={(rule.names ?? []).join(', ')}
                        onChange={(e) =>
                          updateCostRule(idx, {
                            names: e.target.value
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })
                        }
                      />
                    ) : (
                      <Input
                        placeholder="Prefix"
                        value={rule.value ?? ''}
                        onChange={(e) => updateCostRule(idx, { value: e.target.value })}
                      />
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => removeCostRule(idx)}
                      aria-label="Remove rule"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Currency item name"
                      value={rule.currency}
                      onChange={(e) => updateCostRule(idx, { currency: e.target.value })}
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={rule.amount}
                      onChange={(e) => updateCostRule(idx, { amount: Number(e.target.value) || 0 })}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={!dirty || updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save score &amp; costs
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
